import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, X, Megaphone, CheckCircle, Clock, Warning, Hourglass, ArrowRight, Copy, Play, Pause, Stop, Timer } from '@phosphor-icons/react';
import { fmtIstTime, todayIst } from '../lib/datetime';
import { useAuth } from '../contexts/AuthContext';
import SearchableSelect from '../components/SearchableSelect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format seconds → HH:MM:SS
const fmtDuration = (s) => {
  const total = Math.max(0, Math.floor(Number(s) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

// Compute live elapsed from intervals — if the last one has no `end`, count from `start` to now.
const liveSeconds = (intervals) => {
  if (!intervals || intervals.length === 0) return 0;
  let total = 0;
  for (const iv of intervals) {
    if (!iv.start) continue;
    const start = new Date(iv.start).getTime();
    const end = iv.end ? new Date(iv.end).getTime() : Date.now();
    total += Math.max(0, (end - start) / 1000);
  }
  return total;
};

const STATUS_META = {
  done:        { l: 'Done',         icon: CheckCircle, bg: '#dcfce7', fg: '#15803d', border: '#86efac' },
  in_progress: { l: 'In Progress',  icon: Clock,       bg: '#dbeafe', fg: '#1d4ed8', border: '#93c5fd' },
  yet_to_start:{ l: 'Yet to Start', icon: Hourglass,   bg: '#f3e8ff', fg: '#6b21a8', border: '#d8b4fe' },
  blocked:     { l: 'Blocked',      icon: Warning,     bg: '#fee2e2', fg: '#b91c1c', border: '#fca5a5' },
};

const todayStr = todayIst;

export default function StandupReportPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [date, setDate] = useState(todayStr());
  const [myStandup, setMyStandup] = useState(null);
  const [items, setItems] = useState([{ text: '', status: 'in_progress' }]);
  const [blockers, setBlockers] = useState('');
  const [saving, setSaving] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  // ⏱ TIMERS — keyed by `${user_id}|${task_text}` so we can render the right pill on every task
  const [myTimers, setMyTimers] = useState([]);        // current user's timers for `date`
  const [tick, setTick] = useState(0);                  // re-render every second
  const tickRef = useRef();
  // Admin-only — filter team feed
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filterDept, setFilterDept] = useState('all');
  const [filterEmp, setFilterEmp] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [carrying, setCarrying] = useState(false);
  const [importingCsrm, setImportingCsrm] = useState(false);
  // ⏸ IDLE DETECTION — pause running timer after N mins of no keyboard/mouse activity
  const idleMinutes = 10;
  const lastActivityRef = useRef(Date.now());
  const autoPausedRef = useRef(new Set());   // timer ids we auto-paused in this session

  useEffect(() => {
    const bump = () => { lastActivityRef.current = Date.now(); };
    const evts = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];
    evts.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => evts.forEach((e) => window.removeEventListener(e, bump));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [
        axios.get(`${API_URL}/api/my/standup`, { params: { date }, withCredentials: true }),
        axios.get(`${API_URL}/api/standups`, { params: { date }, withCredentials: true }),
        axios.get(`${API_URL}/api/clients`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/projects`, { withCredentials: true }).catch(() => ({ data: [] })),
      ];
      if (isAdmin) {
        reqs.push(axios.get(`${API_URL}/api/departments`, { withCredentials: true }));
        reqs.push(axios.get(`${API_URL}/api/employees`, { withCredentials: true }));
      }
      const results = await Promise.all(reqs);
      const [mine, all, cl, pr, depts, emps] = results;
      setMyStandup(mine.data || null);
      if (mine.data?.items?.length) {
        setItems(mine.data.items.map((i) => ({
          text: i.text,
          status: i.status,
          client_id: i.client_id || '',
          client_name: i.client_name || '',
          project_id: i.project_id || '',
          project_name: i.project_name || '',
          csrm_ref: i.csrm_ref || null,
          dmo_ref: i.dmo_ref || null,
          subtasks: Array.isArray(i.subtasks) ? i.subtasks.map((s) => ({ text: s.text || '', status: s.status || 'in_progress' })) : [],
        })));
        setBlockers(mine.data.blockers || '');
      } else {
        setItems([{ text: '', status: 'in_progress', client_id: '', client_name: '', project_id: '', project_name: '', subtasks: [] }]);
        setBlockers('');
      }
      setList(all.data || []);
      setClients(cl.data || []);
      setProjects(pr.data || []);
      if (depts) setDepartments(depts.data || []);
      if (emps) setEmployees(emps.data || []);
    } catch (e) {
      toast.error('Failed to load standups: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [date, isAdmin]);

  useEffect(() => { load(); }, [load]);

  // Load my timers for the active date
  const loadMyTimers = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/standup/timers`, { params: { date }, withCredentials: true });
      setMyTimers(r.data || []);
    } catch { /* ignore */ }
  }, [date]);

  useEffect(() => { loadMyTimers(); }, [loadMyTimers]);

  // 1-second tick to refresh live HH:MM:SS counters + auto-pause when idle
  useEffect(() => {
    tickRef.current = setInterval(() => {
      setTick((t) => (t + 1) % 100000);
      // ── Idle-auto-pause: if no user activity for N mins, pause any running timer
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs > idleMinutes * 60 * 1000) {
        const running = (myTimers || []).find((t) => t.status === 'running');
        if (running && !autoPausedRef.current.has(running.id)) {
          autoPausedRef.current.add(running.id);
          axios.post(`${API_URL}/api/standup/timer/${running.id}/pause`, {}, { withCredentials: true })
            .then(() => { toast.info(`⏸ Timer auto-paused — no activity for ${idleMinutes} mins`); loadMyTimers(); })
            .catch(() => {});
        }
      }
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [myTimers, loadMyTimers]);   // eslint-disable-line react-hooks/exhaustive-deps

  const myTimerByText = useMemo(() => {
    const m = {};
    for (const t of myTimers) m[t.task_text] = t;
    return m;
  }, [myTimers]);

  const handleTimerStart = async (task) => {
    if (!task || !task.text || !task.text.trim()) { toast.error('Save the task first before starting its timer'); return; }
    try {
      await axios.post(`${API_URL}/api/standup/timer/start`, {
        task_text: task.text,
        date,
        client_id: task.client_id || '',
        client_name: task.client_name || '',
        project_id: task.project_id || '',
        project_name: task.project_name || '',
      }, { withCredentials: true });
      await loadMyTimers();
      toast.success(`Started: ${task.text.slice(0, 40)}`);
    } catch (e) {
      toast.error('Failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleTimerPause = async (timerId) => {
    try { await axios.post(`${API_URL}/api/standup/timer/${timerId}/pause`, {}, { withCredentials: true }); await loadMyTimers(); }
    catch (e) { toast.error('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const handleTimerStop = async (timerId) => {
    if (!window.confirm('Stop this timer? Total recorded time is preserved; you can start a new session later (it will accumulate).')) return;
    try { await axios.post(`${API_URL}/api/standup/timer/${timerId}/stop`, {}, { withCredentials: true }); await loadMyTimers(); }
    catch (e) { toast.error('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  // Renders the timer pill + Start/Pause/Stop controls beside a task (own tasks only)
  const TimerControl = ({ task }) => {
    const t = myTimerByText[task.text];
    const running = t && t.status === 'running';
    const elapsed = t ? liveSeconds(t.intervals) : 0;
    void tick; // force re-render every second
    return (
      <div className="flex items-center gap-1.5" data-testid={`task-timer-${(task.text || '').replace(/\s+/g, '_').slice(0, 30)}`}>
        {t && (
          <span
            className={`text-[11px] font-mono font-bold px-1.5 py-0.5 rounded tabular-nums whitespace-nowrap ${
              running ? 'bg-emerald-100 text-emerald-700' :
              t.status === 'paused' ? 'bg-amber-100 text-amber-700' :
              'bg-slate-100 text-slate-700'
            }`}
            title={`${running ? 'Running' : t.status} — total ${fmtDuration(elapsed)}`}
          >
            {fmtDuration(elapsed)}
          </span>
        )}
        {!running ? (
          <button
            type="button"
            onClick={() => handleTimerStart(task)}
            className="p-1 rounded hover:bg-emerald-50 text-emerald-700"
            title={t ? 'Resume' : 'Start timer'}
            data-testid="timer-start"
          ><Play size={14} weight="fill" /></button>
        ) : (
          <button
            type="button"
            onClick={() => handleTimerPause(t.id)}
            className="p-1 rounded hover:bg-amber-50 text-amber-700"
            title="Pause"
            data-testid="timer-pause"
          ><Pause size={14} weight="fill" /></button>
        )}
        {t && t.status !== 'stopped' && (
          <button
            type="button"
            onClick={() => handleTimerStop(t.id)}
            className="p-1 rounded hover:bg-rose-50 text-rose-600"
            title="Stop"
            data-testid="timer-stop"
          ><Stop size={14} weight="fill" /></button>
        )}
      </div>
    );
  };


  // Lookup helpers — link standups to employees → departments (admin only)
  const empById = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);
  const deptById = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d])), [departments]);

  const filteredList = useMemo(() => {
    return (list || []).filter((s) => {
      if (filterEmp !== 'all' && s.user_id !== filterEmp && s.employee_id !== filterEmp) return false;
      if (filterDept !== 'all') {
        const emp = empById[s.employee_id] || empById[s.user_id];
        if (!emp || emp.department_id !== filterDept) return false;
      }
      if (filterStatus !== 'all') {
        const has = (s.items || []).some((it) => it.status === filterStatus);
        const hasBlockers = (s.blockers || '').trim().length > 0;
        if (filterStatus === 'blocked' && !has && !hasBlockers) return false;
        if (filterStatus !== 'blocked' && !has) return false;
      }
      return true;
    });
  }, [list, filterDept, filterEmp, filterStatus, empById]);

  const groupedByDept = useMemo(() => {
    if (!isAdmin) return [];
    const buckets = {};
    for (const s of filteredList) {
      const emp = empById[s.employee_id] || empById[s.user_id];
      const dept = (emp && deptById[emp.department_id]) || { id: '__no_dept__', name: 'No department' };
      if (!buckets[dept.id]) buckets[dept.id] = { dept, rows: [] };
      buckets[dept.id].rows.push(s);
    }
    return Object.values(buckets).sort((a, b) => a.dept.name.localeCompare(b.dept.name));
  }, [filteredList, empById, deptById, isAdmin]);

  const copyStandup = (s) => {
    const lines = [];
    lines.push(`*${(s.employee_name || '(unnamed)').toUpperCase()}* — ${fmtIstTime(s.updated_at || s.created_at || '') || ''}`);
    (s.items || []).forEach((it, i) => {
      const status = (STATUS_META[it.status] || STATUS_META.in_progress).l.toUpperCase();
      const clientPart = it.client_name ? ` (Client: ${it.client_name})` : '';
      lines.push(`${i + 1}. [${status}] ${it.text}${clientPart}`);
    });
    if (s.blockers) lines.push(`\nBlockers: ${s.blockers}`);
    const text = lines.join('\n');
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(
        () => toast.success('Standup copied to clipboard'),
        () => toast.error('Copy failed — please copy manually')
      );
    } else {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); toast.success('Standup copied'); } catch { toast.error('Copy failed'); }
      document.body.removeChild(ta);
    }
  };

  const renderStandupCard = (s) => {
    const emp = empById[s.employee_id] || empById[s.user_id];
    const deptName = (emp && deptById[emp.department_id]?.name) || '';
    return (
      <Card key={s.id} className="p-3" data-testid={`standup-card-${s.id}`}>
        <div className="flex items-center justify-between border-b pb-2 mb-2">
          <div>
            <div className="text-sm font-bold" style={{ color: '#1a2e4a' }}>
              {s.employee_name || '(unnamed)'}
            </div>
            {deptName && <div className="text-[10px] text-gray-500">{deptName}</div>}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => copyStandup(s)}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-emerald-600 transition"
              title="Copy all items to clipboard"
              data-testid={`standup-copy-${s.id}`}
            >
              <Copy size={14} />
            </button>
            <div className="text-[10px] text-gray-400">
              {fmtIstTime(s.updated_at || s.created_at || '')}
            </div>
          </div>
        </div>
        <ol className="space-y-1.5">
          {(s.items || []).map((it, i) => {
            const sm = STATUS_META[it.status] || STATUS_META.in_progress;
            const SIcon = sm.icon;
            return (
              <li key={i} className="flex items-start gap-2 text-xs">
                <span className="text-gray-400 font-mono mt-0.5 w-4">{i + 1}.</span>
                <SIcon size={14} weight="fill" style={{ color: sm.fg, marginTop: 2 }} />
                <div className="flex-1">
                  <div className={`${it.status === 'done' ? 'text-gray-500 line-through' : 'text-gray-800'}`}>{it.text}</div>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {it.client_name && (
                      <span className="text-[10px] inline-block px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-100 text-emerald-700">
                        🤝 {it.client_name}
                      </span>
                    )}
                    {it.project_name && (
                      <span className="text-[10px] inline-block px-1.5 py-0.5 rounded bg-violet-50 border border-violet-100 text-violet-700">
                        📁 {it.project_name}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase" style={{ background: sm.bg, color: sm.fg }}>
                  {sm.l}
                </span>
              </li>
            );
          })}
        </ol>
        {s.blockers && (
          <div className="mt-2 pt-2 border-t border-rose-100 text-[11px] text-rose-700 bg-rose-50 rounded px-2 py-1">
            <strong>⛔ Blockers:</strong> {s.blockers}
          </div>
        )}
      </Card>
    );
  };

  const addItem = () => setItems((s) => [...s, { text: '', status: 'in_progress', client_id: '', client_name: '', project_id: '', project_name: '', subtasks: [] }]);
  const removeItem = (idx) => setItems((s) => s.length === 1 ? [{ text: '', status: 'in_progress', client_id: '', client_name: '', project_id: '', project_name: '', subtasks: [] }] : s.filter((_, i) => i !== idx));
  const updateItem = (idx, patch) => setItems((s) => s.map((it, i) => i === idx ? { ...it, ...patch } : it));

  // ── Subtask helpers — keyed under items[parentIdx].subtasks[subIdx] ──
  const addSubtask = (parentIdx) => setItems((s) => s.map((it, i) => i === parentIdx
    ? { ...it, subtasks: [...(it.subtasks || []), { text: '', status: 'in_progress' }] }
    : it));
  const updateSubtask = (parentIdx, subIdx, patch) => setItems((s) => s.map((it, i) => i === parentIdx
    ? { ...it, subtasks: (it.subtasks || []).map((sub, si) => si === subIdx ? { ...sub, ...patch } : sub) }
    : it));
  const removeSubtask = (parentIdx, subIdx) => setItems((s) => s.map((it, i) => i === parentIdx
    ? { ...it, subtasks: (it.subtasks || []).filter((_, si) => si !== subIdx) }
    : it));

  // Compose parent › subtask key for the timer so the productivity report groups them under the parent.
  const composeSubtaskText = (parentText, subText) => `${(parentText || '').trim()} › ${(subText || '').trim()}`;

  const save = async () => {
    const clean = items.filter((i) => (i.text || '').trim());
    if (clean.length === 0) { toast.error('Add at least one task'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/my/standup`, { date, items: clean, blockers }, { withCredentials: true });
      toast.success(myStandup ? 'Standup updated' : 'Standup posted');
      load();
    } catch (e) {
      toast.error('Failed: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const carryForward = async () => {
    if (!window.confirm("Copy all non-Done tasks from yesterday's standup to today? (Blocked & Yet-to-Start items will reset to 'Yet to Start' on the new day. Duplicates are skipped.)")) return;
    setCarrying(true);
    try {
      const r = await axios.post(`${API_URL}/api/my/standup/carry-forward`,
        { to_date: date },
        { withCredentials: true });
      toast.success(r.data?.message || `Carried ${r.data?.added || 0} task(s) forward`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
    setCarrying(false);
  };

  const importCsrmCommitments = async () => {
    setImportingCsrm(true);
    try {
      const [csrmR, dmoR] = await Promise.all([
        axios.get(`${API_URL}/api/csrm/my-standup-items`, { withCredentials: true }),
        axios.get(`${API_URL}/api/dmo/my-standup-items`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      const combined = [...(csrmR.data || []), ...(dmoR.data || [])];
      if (combined.length === 0) {
        toast.info('No CSRM/DMO commitments pending for today — you\'re all caught up 🎉');
        setImportingCsrm(false);
        return;
      }
      const existingTexts = new Set(items.map((it) => (it.text || '').trim().toLowerCase()));
      const toAdd = combined
        .filter((c) => !existingTexts.has((c.text || '').trim().toLowerCase()))
        .map((c) => ({
          text: c.text,
          status: c.status || 'in_progress',
          client_id: c.client_id || '',
          client_name: c.client_name || '',
          project_id: '',
          project_name: '',
          csrm_ref: c.csrm_ref || null,
          dmo_ref: c.dmo_ref || null,
        }));
      if (toAdd.length === 0) {
        toast.info(`${combined.length} commitment(s) found — all already in your standup.`);
        setImportingCsrm(false);
        return;
      }
      const trimmedItems = items.filter((it) => (it.text || '').trim());
      setItems([...trimmedItems, ...toAdd]);
      toast.success(`Imported ${toAdd.length} commitment${toAdd.length === 1 ? '' : 's'} — review and Post Standup when ready.`);
    } catch (e) {
      toast.error('Import failed: ' + (e.response?.data?.detail || e.message));
    }
    setImportingCsrm(false);
  };

  return (
    <div className="space-y-4" data-testid="standup-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#1a2e4a' }}>
            <Megaphone size={24} weight="duotone" /> Daily Standup
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Post your daily updates — what tasks you worked on and their current status. Like a Microsoft Teams standup, but tracked in PayrollHub.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="standup-date" />
          <Button variant="outline" size="sm" onClick={() => setDate(todayStr())} data-testid="standup-today">Today</Button>
        </div>
      </div>

      {/* My Standup — input/edit panel */}
      <Card className="p-4 border-l-4" style={{ borderLeftColor: '#1D9E75' }} data-testid="my-standup-card">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold uppercase tracking-wider" style={{ color: '#1a2e4a' }}>
            My Standup · {date}
          </div>
          {myStandup && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
              POSTED · last updated {fmtIstTime(myStandup.updated_at || '')}
            </span>
          )}
        </div>

        <div className="space-y-3">
          {items.map((it, idx) => {
            const sm = STATUS_META[it.status] || STATUS_META.in_progress;
            return (
              <div key={idx} className="space-y-1.5" data-testid={`standup-item-${idx}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 text-xs font-mono text-gray-400 mt-2 w-5 text-right">{idx + 1}.</div>
                  <div className="flex-1">
                    <Input
                      placeholder="e.g. WordPress homepage — finished hero section + mobile responsive"
                      value={it.text}
                      onChange={(e) => updateItem(idx, { text: e.target.value })}
                      data-testid={`standup-item-text-${idx}`}
                    />
                  </div>
                  <select
                    className="border rounded px-2 py-2 text-xs bg-white font-semibold"
                    style={{ background: sm.bg, color: sm.fg, borderColor: sm.border }}
                    value={it.status}
                    onChange={(e) => updateItem(idx, { status: e.target.value })}
                    data-testid={`standup-item-status-${idx}`}
                  >
                    <option value="done">✓ Done</option>
                    <option value="in_progress">↻ In Progress</option>
                    <option value="yet_to_start">⧗ Yet to Start</option>
                    <option value="blocked">⛔ Blocked</option>
                  </select>
                  {(it.text || '').trim() && !((it.subtasks || []).some((s) => (s.text || '').trim())) && <TimerControl task={it} />}
                  <button
                    onClick={() => removeItem(idx)}
                    className="text-rose-500 hover:text-rose-700 p-2"
                    title="Remove"
                    data-testid={`standup-item-remove-${idx}`}
                  >
                    <X size={16} />
                  </button>
                </div>
                {/* Client picker (optional) — searchable */}
                <div className="flex items-center gap-2 pl-7">
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold whitespace-nowrap">Client</span>
                  <div className="flex-1 max-w-xs">
                    <SearchableSelect
                      testId={`standup-item-client-${idx}`}
                      value={it.client_id || ''}
                      onChange={(id) => {
                        const c = clients.find((x) => x.id === id);
                        const nm = c ? (`${c.first_name || ''} ${c.last_name || ''}`.trim() || c.company_name || '') : '';
                        updateItem(idx, { client_id: id, client_name: nm });
                      }}
                      placeholder="— (internal / no client)"
                      options={clients.map((c) => {
                        const fullName = `${c.first_name || ''} ${c.last_name || ''}`.trim();
                        return {
                          value: c.id,
                          label: fullName || c.company_name || '(unnamed)',
                          sublabel: c.company_name && fullName ? c.company_name : (c.email || ''),
                          search: `${fullName} ${c.company_name || ''} ${c.email || ''} ${c.gstin || ''}`,
                        };
                      })}
                    />
                  </div>
                </div>
                {/* Project picker (optional) — searchable */}
                <div className="flex items-center gap-2 pl-7">
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold whitespace-nowrap">Project</span>
                  <div className="flex-1 max-w-xs">
                    <SearchableSelect
                      testId={`standup-item-project-${idx}`}
                      value={it.project_id || ''}
                      onChange={(id) => {
                        const p = projects.find((x) => x.id === id);
                        updateItem(idx, { project_id: id, project_name: p?.name || '' });
                      }}
                      placeholder="— (no project)"
                      options={projects.map((p) => ({
                        value: p.id,
                        label: p.name,
                        sublabel: p.status || '',
                        search: `${p.name || ''} ${p.status || ''} ${p.description || ''}`,
                      }))}
                    />
                  </div>
                </div>
                {/* Sub-tasks — indented children with their own timer */}
                {((it.subtasks || []).length > 0) && (
                  <div className="ml-7 pl-3 mt-1 space-y-1 border-l-2 border-indigo-100" data-testid={`standup-subtasks-${idx}`}>
                    {(it.subtasks || []).map((sub, si) => {
                      const subKey = composeSubtaskText(it.text, sub.text);
                      const subT = myTimerByText[subKey];
                      const sm = STATUS_META[sub.status] || STATUS_META.in_progress;
                      return (
                        <div key={si} className="flex items-start gap-2" data-testid={`standup-subtask-${idx}-${si}`}>
                          <div className="flex-shrink-0 text-[10px] font-mono text-indigo-400 mt-2.5 w-6 text-right">↳ {si + 1}</div>
                          <div className="flex-1">
                            <Input
                              placeholder="sub-task (e.g. wireframe mobile menu)"
                              value={sub.text}
                              onChange={(e) => updateSubtask(idx, si, { text: e.target.value })}
                              className="h-8 text-xs"
                              data-testid={`standup-subtask-text-${idx}-${si}`}
                            />
                          </div>
                          <select
                            className="border rounded px-1.5 py-1 text-[10px] bg-white font-semibold h-8"
                            style={{ background: sm.bg, color: sm.fg, borderColor: sm.border }}
                            value={sub.status}
                            onChange={(e) => updateSubtask(idx, si, { status: e.target.value })}
                            data-testid={`standup-subtask-status-${idx}-${si}`}
                          >
                            <option value="done">✓ Done</option>
                            <option value="in_progress">↻ Progress</option>
                            <option value="yet_to_start">⧗ To do</option>
                            <option value="blocked">⛔ Blocked</option>
                          </select>
                          {(sub.text || '').trim() && (it.text || '').trim() && (
                            <TimerControl task={{
                              text: subKey,
                              client_id: it.client_id,
                              client_name: it.client_name,
                              project_id: it.project_id,
                              project_name: it.project_name,
                            }} />
                          )}
                          {!subT && (
                            <button
                              onClick={() => removeSubtask(idx, si)}
                              className="text-rose-400 hover:text-rose-700 p-1"
                              title="Remove sub-task"
                              data-testid={`standup-subtask-remove-${idx}-${si}`}
                            >
                              <X size={13} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="ml-7 pl-3">
                  <button
                    type="button"
                    onClick={() => addSubtask(idx)}
                    className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600 hover:text-indigo-800 mt-1"
                    data-testid={`standup-add-subtask-${idx}`}
                  >
                    + sub-task
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={addItem} data-testid="standup-add-item">
            <Plus size={14} className="mr-1" /> Add Task
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={carryForward}
            disabled={carrying}
            data-testid="standup-carry-forward"
            title="Copy all non-Done tasks from yesterday's standup to today"
          >
            <ArrowRight size={14} className="mr-1" />
            {carrying ? 'Carrying…' : 'Carry forward from yesterday'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={importCsrmCommitments}
            disabled={importingCsrm}
            data-testid="standup-import-csrm"
            title="Auto-add today's CSRM tasks &amp; DMO commitments (social posts / SEO) assigned to you"
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
          >
            <Megaphone size={14} className="mr-1" />
            {importingCsrm ? 'Importing…' : 'Import today\'s commitments (CSRM + DMO)'}
          </Button>
        </div>

        <div className="mt-4">
          <Label className="text-xs">Blockers / Notes (optional)</Label>
          <textarea
            className="w-full border rounded px-3 py-2 text-sm min-h-[60px] mt-1"
            value={blockers}
            onChange={(e) => setBlockers(e.target.value)}
            placeholder="Anything blocking progress? Things the team should know?"
            data-testid="standup-blockers"
          />
        </div>

        <div className="flex justify-end mt-3">
          <Button onClick={save} disabled={saving} data-testid="standup-save">
            {saving ? 'Saving…' : (myStandup ? 'Update Standup' : 'Post Standup')}
          </Button>
        </div>
      </Card>

      {/* Team / All standups for the day */}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="text-sm font-bold uppercase tracking-wider" style={{ color: '#1a2e4a' }}>
            Team Updates · {date} {list.length > 0 && <span className="text-xs font-normal text-gray-500 normal-case">({filteredList.length} of {list.length})</span>}
          </div>
          {isAdmin && (
            <div className="flex flex-wrap gap-2 items-center text-xs">
              <select
                value={filterDept}
                onChange={(e) => { setFilterDept(e.target.value); setFilterEmp('all'); }}
                className="border rounded px-2 py-1.5 bg-white"
                data-testid="standup-dept-filter"
              >
                <option value="all">All departments</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <select
                value={filterEmp}
                onChange={(e) => setFilterEmp(e.target.value)}
                className="border rounded px-2 py-1.5 bg-white"
                data-testid="standup-emp-filter"
              >
                <option value="all">All employees</option>
                {employees
                  .filter((e) => filterDept === 'all' || e.department_id === filterDept)
                  .map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border rounded px-2 py-1.5 bg-white"
                data-testid="standup-status-filter"
              >
                <option value="all">All statuses</option>
                <option value="done">Has Done items</option>
                <option value="in_progress">Has In Progress</option>
                <option value="yet_to_start">Has Yet to Start</option>
                <option value="blocked">Has Blockers</option>
              </select>
              {(filterDept !== 'all' || filterEmp !== 'all' || filterStatus !== 'all') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setFilterDept('all'); setFilterEmp('all'); setFilterStatus('all'); }}
                  data-testid="standup-filter-reset"
                >
                  Reset
                </Button>
              )}
            </div>
          )}
        </div>
        {loading ? (
          <Card className="p-6 text-center text-sm text-gray-500">Loading…</Card>
        ) : filteredList.length === 0 ? (
          <Card className="p-8 text-center text-sm text-gray-500" data-testid="standup-empty">
            {list.length === 0 ? `No updates posted for ${date} yet.` : 'No standups match the current filters.'}
          </Card>
        ) : isAdmin && filterDept === 'all' && filterEmp === 'all' ? (
          // Admin default: group by department
          <div className="space-y-4">
            {groupedByDept.map(({ dept, rows }) => (
              <div key={dept.id} data-testid={`standup-dept-group-${dept.id}`}>
                <div className="text-xs font-bold uppercase tracking-wider mb-2 px-2 py-1 rounded bg-slate-100 inline-block" style={{ color: '#1a2e4a' }}>
                  {dept.name} <span className="text-gray-500 font-normal ml-1">({rows.length})</span>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {rows.map((s) => renderStandupCard(s))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filteredList.map((s) => renderStandupCard(s))}
          </div>
        )}
      </div>
    </div>
  );
}
